/**
 * `POST /bql`, parsed per tile rather than all-or-nothing.
 *
 * `warnings[].code` and `error.code` are the two most additive things a query
 * engine grows — a new warning or error code lands in this enum ahead of a
 * client regen, and under the old atomic parse (`parseResponse(
 * BqlResponseSpineSchema, …)`) one tile carrying either threw the WHOLE
 * batch. `bql-transport.ts` coalesces up to `MAX_BATCH_QUERIES` widgets into
 * one request, so that turned one new warning code into an outage for every
 * caller in the chunk — breaking the promise `api.ts`'s `runBqlBatch`
 * docblock makes in writing: "a rejected tile never fails its siblings."
 *
 * ## The envelope is loosened in exactly one place
 *
 * `bqlResponseEnvelopeSchema` types `results` as `unknown[]` instead of the
 * generated discriminated-union array, so each element can be re-gated on
 * its own via `envelope.ts`'s exported `BqlResultSpineSchema` — the SAME
 * per-tile schema the atomic `BqlResponseSpineSchema` composes from, so a
 * tile that parses here parses identically to one that would have parsed
 * under the old whole-array validation.
 *
 * A dropped tile needs no new UI: it simply never appears in the returned
 * `results`, and `bql-transport.ts`'s tile lookup already resolves a
 * `query_id` absent from the response as its own `MISSING_TILE` constant — a
 * dropped tile is structurally indistinguishable from one the server never
 * answered.
 *
 * **Flagged, not fixed (out of this item's scope):** `results.ts`'s
 * `demuxResults` still THROWS on a duplicate `query_id` across the tiles this
 * function returns. That is already prevented client-side — `bql-transport.ts`'s
 * `chunkQueries` never puts two tiles with the same `query_id` in one request —
 * so a per-tile parse here does not need to (and does not) guard against it.
 */
import { z } from 'zod'

import { parseArrayRows, rowKeyOf } from '@/shared/api/utils/partial-contract'

import { BqlContractError, parseResponse } from './contract'
import { BqlResultSpineSchema } from './envelope'

import type { BqlResponseSpine } from './envelope'
import type { DroppedRow } from '@/shared/api/utils/partial-contract'

/** `BqlResponseSpineSchema`'s envelope, with `results` loosened for per-tile re-gating. */
const bqlResponseEnvelopeSchema = z.object({
	results: z.array(z.unknown()),
})

/** A tile's `query_id` when the row is at least shaped enough to have read one, else its array index. */
const tileRowKey = rowKeyOf('query_id')

/**
 * Parse `POST /bql` with per-tile blast radius: a tile that fails
 * `BqlResultSpineSchema` is dropped and reported, every sibling tile in the
 * same batch still returns.
 */
export function parseBqlBatchResponse(
	body: unknown,
	route: string,
): { value: BqlResponseSpine; dropped: DroppedRow[] } {
	const envelope = parseResponse(bqlResponseEnvelopeSchema, body, route)

	const { value: results, dropped } = parseArrayRows(
		envelope.results,
		BqlResultSpineSchema,
		(key, cause) => new BqlContractError(`${route} results.${key}`, cause),
		tileRowKey,
	)

	return { value: { results }, dropped }
}
